import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { EPF, EPFHistory, IAPIEPF } from 'src/model/epf.model';
import { ConstructAPI } from '../app/API/constructAPI';
import { RestApiService } from '../app/API/restapi';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class EPFAdapter {
  private datePipe = inject(DatePipe);

  adapt(item: any): EPF {
    return new EPF(
      item.amount,
      item.cutomerId,
      new Date(item.date),
      item.hasIAccount,
      item.iAccountPassword,
      item.iAccountUsername,
      item.remark
    )
  }

  adaptEPFHistory(item: any): EPFHistory {
    return {
      Id: item.id,
      CustomerId: item.customerId,
      ModifiedBy: item.modified_by,
      ModifiedOn: new Date(item.modified_on),
      OldValue: item.old_value ? this.adapt(JSON.parse(item.old_value)) : undefined,
      NewValue: item.new_value ? this.adapt(JSON.parse(item.new_value)) : undefined
    }
  }

  toApiModel(epf: EPF): IAPIEPF {
    return {
      ...(epf.Date && { date: this.datePipe.transform(epf.Date, 'yyyy-MM-dd') }),
      ...(epf.Amount && { amount: epf.Amount }),
      hasIAccount: epf.HasIAccount,
      iAccountUsername: epf.IAccountUsername,
      iAccountPassword: epf.IAccountPassword,
      remark: epf.Remark
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class EpfService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(EPFAdapter);

  getCustomerEPF(custID: number): Promise<EPF> {
    return new Promise<EPF>((resolve, reject) => {
      this.api.get<EPF>(this.constructApi.getCustomerEPF(custID)).pipe(
        map(data => this.adapter.adapt(data))
      ).subscribe(
        {
          next: response => {
            resolve(response);
          },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get customer epf details: ', err);
            reject(err);
          }
        }
      )
    });
  }

  postCustomerEPF(custID: number, epf: EPF): Promise<EPF> {
    return new Promise<EPF>((resolve, reject) => {

      this.api.post<EPF>(this.constructApi.getUpdateEPFDetails(custID), this.adapter.toApiModel(epf)).subscribe({
        next: response => resolve(this.adapter.adapt(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add epf details: ', err);
          reject(err);
        }
      });
    })
  }

  updateCustomerEPF(custID: number, epf: EPF): Promise<EPF> {
    return new Promise<EPF>((resolve, reject) => {
      this.api.put<EPF>(this.constructApi.getUpdateEPFDetails(custID), this.adapter.toApiModel(epf)).subscribe({
        next: response => {
          resolve(this.adapter.adaptEPFHistory(response).NewValue);
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update epf details: ', err);
          reject(err);
        }
      });
    });
  }

  getCompanyCustomerEPFStatementExistence(compID, custID: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.api.get<boolean>(this.constructApi.getCompanyCustomerEPFStatementExistence(compID, custID)).subscribe({
        next: response => {
          resolve(Boolean(response));
        },
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }

  getCustomerEPFChangeHistory(custID: number): Promise<EPFHistory[]> {
    return new Promise<EPFHistory[]>((resolve, reject) => {
      this.api.get<EPFHistory[]>(this.constructApi.getCustomerEPFChangeHistory(custID)).pipe(
        map(data => data.map(d => this.adapter.adaptEPFHistory(d)))
      ).subscribe({
        next: response => {
          resolve(response);
        },
        error: err => {
          this.api.handleError(err);
          console.error(err);
          reject(err);
        }
      })
    })
  }
}
